import React, { useEffect, useMemo } from "react";
import { autorun } from "mobx";
import { observer } from "mobx-react-lite";
import {
    Alert,
    Box,
    ButtonBase,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AutoAwesomeOutlined,
    CalendarTodayOutlined,
    ChatBubbleOutlineRounded,
    SearchRounded,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxToggleButton from "components/UI/AcxToggleButton";
import AcxVerticalBarGraph from "components/UI/Visualization/AcxVerticalBarGraph";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import moment from "moment";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import OrganizationUsageValueStore, {
    UsageValueTab,
} from "./OrganizationUsageValueStore";
import {
    chatColumns,
    flowColumns,
    searchColumns,
    totalRow,
} from "./usageValueColumns";
import {
    formatBusinessDays,
    formatHours,
    formatNumber,
    formatRangeLabel,
    isAuthentizenEmail,
    percentChange,
    pluralize,
    usageValuePresets,
    UsageValuePresetId,
} from "./usageValueFormat";

type StatItem = {
    label: string;
    value: string;
    sub?: string;
    delta?: number | null;
};

const footnotes: Record<UsageValueTab, string> = {
    chat: "Listening time saved uses the same estimates KBo returns when a user asks about it in chat. Business days assume 8 working hours per day.",
    search: "Emails sent reflect the selected date range. The org total also counts emails from subscriptions that have since been deleted, so it can be higher than the sum of the rows listed.",
    flow: "Emails sent reflect the selected date range. The org total also counts emails from subscriptions that have since been deleted and reports sent manually from KBo Flow, so it can be higher than the sum of the rows listed.",
};

const tableTitles: Record<UsageValueTab, string> = {
    chat: "KBo chat by user",
    search: "Search subscriptions",
    flow: "KBo Flow subscriptions",
};

/** Keeps the shared grid's columns, rows and totals row in step with the selected tab. */
const syncGrid = (store: OrganizationUsageValueStore) => {
    const report = store.report;
    if (store.tab === "chat") {
        const totals = report?.current;
        void store.grid.setColumns(chatColumns);
        store.grid.setRows(
            store.filteredChatUsers.map((row) => ({ ...row, id: row.userId })),
        );
        store.grid.pinnedBottomRows = totals
            ? [
                  totalRow(
                      "name",
                      "Org total",
                      "email",
                      pluralize(totals.activeChatUsers, "user"),
                      {
                          messages: totals.messages,
                          listeningTimeSavedMs: totals.listeningTimeSavedMs,
                          montagesCreated: totals.montagesCreated,
                      },
                  ),
              ]
            : undefined;
    } else if (store.tab === "search") {
        const rows = store.visibleSearchSubscriptions;
        void store.grid.setColumns(searchColumns);
        store.grid.setRows(rows);
        store.grid.pinnedBottomRows = report
            ? [
                  totalRow(
                      "name",
                      "Org total",
                      "totalSub",
                      `${report.current.activeSearchSubscriptions} active subscriptions`,
                      // The period's total, including deactivated subscriptions hidden by the
                      // toggle, so it matches the stat cards above.
                      { emailsSent: report.current.searchEmailsSent },
                  ),
              ]
            : undefined;
    } else {
        const rows = store.visibleFlowSubscriptions;
        void store.grid.setColumns(flowColumns);
        store.grid.setRows(rows);
        store.grid.pinnedBottomRows = report
            ? [
                  totalRow(
                      "userName",
                      "Org total",
                      "totalSub",
                      `${report.current.activeFlowSubscriptions} active subscriptions`,
                      { emailsSent: report.current.flowEmailsSent },
                  ),
              ]
            : undefined;
    }
};

const OrganizationUsageValue = observer(({ id }: { id: string }) => {
    const theme = useTheme();
    const authStore = useStore(AuthStore);
    const store = useMemo(() => new OrganizationUsageValueStore(id), [id]);
    // Only Authenticx employees choose whether their own usage counts; everyone else never sees it.
    const user = authStore.permStore.user;
    const canIncludeAuthentizens = isAuthentizenEmail(
        user?.email ?? user?.userName,
    );

    useEffect(() => {
        void store.load();
        const disposeSync = autorun(() => syncGrid(store));
        return () => {
            disposeSync();
            store.dispose();
        };
    }, [store]);

    // The picker resyncs whenever its defaults change identity, so keep them stable per range.
    const rangeEnd = store.range.end;
    const pickerEndDate = useMemo(
        () => rangeEnd.clone().subtract(1, "day"),
        [rangeEnd],
    );

    const report = store.report;
    const current = report?.current;
    const previous = report?.previous;

    const montageCreators = report?.chatUsers.filter(
        (u) => u.montagesCreated > 0,
    );
    const unattributedMontages =
        (current?.montagesCreated ?? 0) -
        (montageCreators ?? []).reduce((sum, u) => sum + u.montagesCreated, 0);
    // Montages whose creator email matches no single user still count in the total, so say so
    // rather than implying the identified users made all of them.
    const montageSub =
        unattributedMontages > 0
            ? `${pluralize(
                  montageCreators?.length ?? 0,
                  "identified user",
              )} · ${formatNumber(unattributedMontages)} unattributed`
            : `Across ${pluralize(montageCreators?.length ?? 0, "user")}`;

    const kpis: StatItem[] =
        current && previous
            ? [
                  {
                      label: "Time saved with KBo",
                      value: formatHours(current.listeningTimeSavedMs),
                      sub: `${formatBusinessDays(
                          current.listeningTimeSavedMs,
                      )} · listening time`,
                      delta: percentChange(
                          current.listeningTimeSavedMs,
                          previous.listeningTimeSavedMs,
                      ),
                  },
                  {
                      label: "KBo messages",
                      value: formatNumber(current.messages),
                      sub: pluralize(current.activeChatUsers, "active user"),
                      delta: percentChange(current.messages, previous.messages),
                  },
                  {
                      label: "Active subscriptions",
                      value: formatNumber(
                          current.activeSearchSubscriptions +
                              current.activeFlowSubscriptions,
                      ),
                      // A current snapshot with no delta: past counts can't be reconstructed
                      // reliably (approximate deactivation dates, hard-deleted KBo Flow rows).
                      sub: `${current.activeSearchSubscriptions} Search · ${current.activeFlowSubscriptions} KBo Flow · at period end`,
                  },
                  {
                      label: "Emails delivered",
                      value: formatNumber(
                          current.searchEmailsSent + current.flowEmailsSent,
                      ),
                      sub: `${formatNumber(
                          current.searchEmailsSent,
                      )} Search · ${formatNumber(
                          current.flowEmailsSent,
                      )} KBo Flow`,
                      delta: percentChange(
                          current.searchEmailsSent + current.flowEmailsSent,
                          previous.searchEmailsSent + previous.flowEmailsSent,
                      ),
                  },
                  {
                      label: "Montages created",
                      value: formatNumber(current.montagesCreated),
                      sub: montageSub,
                      delta: percentChange(
                          current.montagesCreated,
                          previous.montagesCreated,
                      ),
                  },
              ]
            : [];

    const activeSearch =
        report?.searchSubscriptions.filter((s) => s.isActive) ?? [];
    const tabStats: StatItem[] = !report
        ? []
        : store.tab === "chat"
        ? [
              {
                  label: "Messages",
                  value: formatNumber(report.current.messages),
                  sub: pluralize(report.current.activeChatUsers, "active user"),
              },
              {
                  label: "Listening time saved",
                  value: formatHours(report.current.listeningTimeSavedMs),
                  sub: formatBusinessDays(report.current.listeningTimeSavedMs),
              },
              {
                  label: "Montages created",
                  value: formatNumber(report.current.montagesCreated),
              },
          ]
        : store.tab === "search"
        ? [
              {
                  label: "Active subscriptions",
                  value: formatNumber(activeSearch.length),
              },
              {
                  label: "Subscribers",
                  value: formatNumber(
                      new Set(activeSearch.map((s) => s.ownerEmail)).size,
                  ),
              },
              {
                  label: "With KBo analysis",
                  value: `${
                      activeSearch.filter((s) => s.kboAnalysisEnabled).length
                  } of ${activeSearch.length}`,
              },
              {
                  label: "Emails sent",
                  value: formatNumber(report.current.searchEmailsSent),
              },
          ]
        : [
              {
                  label: "Active subscriptions",
                  value: formatNumber(report.current.activeFlowSubscriptions),
              },
              {
                  label: "Emails sent",
                  value: formatNumber(report.current.flowEmailsSent),
              },
              {
                  label: "Inactive subscriptions",
                  value: formatNumber(
                      report.flowSubscriptions.filter((s) => !s.isActive)
                          .length,
                  ),
              },
          ];

    const tabs: {
        id: UsageValueTab;
        label: string;
        summary: string;
        icon: React.ReactNode;
    }[] = [
        {
            id: "chat",
            label: "KBo chat",
            summary: current
                ? `${pluralize(
                      current.activeChatUsers,
                      "user",
                  )} · ${formatHours(current.listeningTimeSavedMs)} saved`
                : "—",
            icon: <ChatBubbleOutlineRounded fontSize="small" />,
        },
        {
            id: "search",
            label: "Search subscriptions",
            summary: current
                ? `${current.activeSearchSubscriptions} active · ${formatNumber(
                      current.searchEmailsSent,
                  )} emails sent`
                : "—",
            icon: <SearchRounded fontSize="small" />,
        },
        {
            id: "flow",
            label: "KBo Flow subscriptions",
            summary: current
                ? `${current.activeFlowSubscriptions} active · ${formatNumber(
                      current.flowEmailsSent,
                  )} emails sent`
                : "—",
            icon: <AutoAwesomeOutlined fontSize="small" />,
        },
    ];

    const inactiveCount =
        store.tab === "search"
            ? report?.searchSubscriptions.filter((s) => !s.isActive).length ?? 0
            : report?.flowSubscriptions.filter((s) => !s.isActive).length ?? 0;

    const chartData = report
        ? [
              ["Period", "Listening time saved (h)"],
              ...report.timeSavedSeries.map((bucket) => [
                  // Buckets are UTC-truncated weeks/months; format in UTC so labels stay on the bucket start.
                  moment
                      .utc(bucket.bucketStart)
                      .format(
                          report.seriesBucket === "month" ? "MMM" : "MMM D",
                      ),
                  Math.round(bucket.listeningTimeSavedMs / 360_000) / 10,
              ]),
          ]
        : undefined;

    const cardSx = {
        border: `1px solid ${theme.palette.gray[200]}`,
        borderRadius: 2,
        backgroundColor: theme.palette.white.main,
    };

    const statCard = (stat: StatItem) => (
        <Box key={stat.label} sx={{ ...cardSx, p: 2, minWidth: 0 }}>
            <Typography
                sx={{ fontSize: "12px", color: theme.palette.gray[600] }}
            >
                {stat.label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "24px",
                    fontFamily: "Inter Bold",
                    color: theme.palette.gray[900],
                    mt: 0.5,
                }}
            >
                {stat.value}
            </Typography>
            {stat.sub && (
                <Typography
                    sx={{ fontSize: "12px", color: theme.palette.gray[500] }}
                >
                    {stat.sub}
                </Typography>
            )}
            {stat.delta !== undefined && stat.delta !== null && (
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontFamily: "Inter Bold",
                        mt: 1,
                        color:
                            stat.delta >= 0
                                ? theme.palette.green.main
                                : theme.palette.red.main,
                    }}
                >
                    {stat.delta >= 0 ? "▲" : "▼"} {Math.abs(stat.delta)}% vs
                    previous period
                </Typography>
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                p: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1400px",
                display: "flex",
                flexDirection: "column",
                gap: 3,
            }}
        >
            <OrganizationBreadcrumbs
                pageTitle="Usage & Value"
                sectionLabel="Management"
            />

            <Box>
                <Typography
                    sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        color: theme.palette.gray[700],
                    }}
                >
                    Usage & Value
                </Typography>
                <Typography
                    sx={{
                        color: theme.palette.gray[500],
                        fontSize: "14px",
                        mt: 0.5,
                    }}
                >
                    Org-wide adoption and time saved across KBo chat, Search
                    subscriptions and KBo Flow. Use it to prepare QBRs and
                    renewal conversations.
                </Typography>
            </Box>

            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
                <AcxToggleButton
                    id="usage-value-date-presets"
                    exclusive
                    sx={{
                        flexWrap: "wrap",
                        "& .MuiToggleButton-root": {
                            flex: "0 0 auto",
                            px: 2,
                        },
                    }}
                    defaultValue={store.preset}
                    items={usageValuePresets.map((preset) => ({
                        id: `usage-value-preset-${preset.id}`,
                        value: preset.id,
                        element: preset.label,
                    }))}
                    onChange={(value: UsageValuePresetId | null) => {
                        if (!value) return;
                        if (value === "custom") store.chooseCustom();
                        else store.selectPreset(value);
                    }}
                />
                {store.preset === "custom" && (
                    <AcxDateRangeInput
                        hideLabel
                        defaultStartDate={store.range.start}
                        defaultEndDate={pickerEndDate}
                        selectOnComplete
                        onSelect={(start, end) =>
                            store.selectCustomRange(start, end)
                        }
                    />
                )}
                <Stack direction="row" alignItems="center" gap={1}>
                    <CalendarTodayOutlined
                        fontSize="small"
                        sx={{ color: theme.palette.gray[500] }}
                    />
                    <Typography
                        sx={{ fontSize: "13px", fontFamily: "Inter Bold" }}
                    >
                        {formatRangeLabel(store.range)}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            color: theme.palette.gray[500],
                        }}
                    >
                        · compared with the previous period of equal length
                    </Typography>
                </Stack>
                {canIncludeAuthentizens && (
                    <FormControlLabel
                        sx={{ m: 0, gap: 1 }}
                        control={
                            <Checkbox
                                id="usage-value-include-authentizens"
                                size="small"
                                color="primary"
                                sx={{ p: 0 }}
                                checked={store.includeAuthentizens}
                                onChange={() => store.toggleAuthentizens()}
                            />
                        }
                        label={
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontFamily: "Inter Bold",
                                }}
                            >
                                Include Authentizens
                            </Typography>
                        }
                        title="Authenticx employee accounts are left out of every total and table unless this is on"
                    />
                )}
            </Stack>

            {store.error && <Alert severity="error">{store.error}</Alert>}

            <Box
                aria-label="Org-wide summary"
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 2,
                    opacity: store.isLoading ? 0.6 : 1,
                }}
            >
                {kpis.map(statCard)}
            </Box>

            <Box>
                <Typography
                    sx={{
                        fontSize: "18px",
                        fontFamily: "Inter Bold",
                        color: theme.palette.gray[900],
                    }}
                >
                    Usage by area
                </Typography>
                <Typography
                    sx={{ fontSize: "13px", color: theme.palette.gray[500] }}
                >
                    Choose an area to see detail by user
                </Typography>
            </Box>

            <Box
                role="tablist"
                aria-label="Usage areas"
                sx={{
                    ...cardSx,
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    overflow: "hidden",
                }}
            >
                {tabs.map((tab) => {
                    const selected = store.tab === tab.id;
                    return (
                        <ButtonBase
                            key={tab.id}
                            role="tab"
                            aria-selected={selected}
                            onClick={() => store.selectTab(tab.id)}
                            sx={{
                                justifyContent: "flex-start",
                                gap: 1.5,
                                minHeight: 72,
                                px: 2.5,
                                py: 1.5,
                                textAlign: "left",
                                backgroundColor: selected
                                    ? theme.palette.blue.background
                                    : theme.palette.white.main,
                                boxShadow: `inset 0 -3px 0 ${
                                    selected
                                        ? theme.palette.primary.main
                                        : "transparent"
                                }`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    backgroundColor: selected
                                        ? theme.palette.primary.main
                                        : theme.palette.gray[100],
                                    color: selected
                                        ? theme.palette.white.main
                                        : theme.palette.gray[600],
                                }}
                            >
                                {tab.icon}
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "15px",
                                        fontFamily: "Inter Bold",
                                        color: selected
                                            ? theme.palette.primary.dark
                                            : theme.palette.gray[900],
                                    }}
                                >
                                    {tab.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: theme.palette.gray[500],
                                    }}
                                >
                                    {tab.summary}
                                </Typography>
                            </Box>
                        </ButtonBase>
                    );
                })}
            </Box>

            <Box
                role="tabpanel"
                aria-label={tableTitles[store.tab]}
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 2,
                    }}
                >
                    {tabStats.map(statCard)}
                </Box>

                {store.tab === "chat" && chartData && (
                    <Box sx={{ ...cardSx, p: 2 }}>
                        <Typography
                            sx={{
                                fontSize: "15px",
                                fontFamily: "Inter Bold",
                                color: theme.palette.gray[900],
                            }}
                        >
                            Time saved over time
                        </Typography>
                        {report && report.current.listeningTimeSavedMs > 0 ? (
                            <AcxVerticalBarGraph
                                data={chartData}
                                legendPosition="none"
                                yTitle="Hours"
                                chartHeight="260px"
                                chartAreaHeight="70%"
                                chartAreaLeftOffset={64}
                                chartAreaWidth="88%"
                                showGrid
                                fontSize="11"
                                fontItalic={false}
                                dataOpacity={1}
                                colorScheme={[theme.palette.primary.main]}
                            />
                        ) : (
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    color: theme.palette.gray[500],
                                    py: 4,
                                    textAlign: "center",
                                }}
                            >
                                No listening time saved in this period.
                            </Typography>
                        )}
                    </Box>
                )}

                <Box>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        gap={2}
                        sx={{ mb: 1 }}
                    >
                        <Typography
                            sx={{
                                fontSize: "15px",
                                fontFamily: "Inter Bold",
                                color: theme.palette.gray[900],
                            }}
                        >
                            {tableTitles[store.tab]}
                        </Typography>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            alignItems="center"
                            gap={2}
                        >
                            {store.tab === "chat" && (
                                <AcxMainTextField
                                    id="usage-value-find-user"
                                    placeholderText="Find user by name, email or role"
                                    value={store.query}
                                    onChange={(event) =>
                                        store.setQuery(event.target.value)
                                    }
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchRounded fontSize="small" />
                                        </InputAdornment>
                                    }
                                />
                            )}
                            {store.tab !== "chat" && (
                                <FormControlLabel
                                    control={
                                        <AcxSwitch
                                            checked={store.showInactive}
                                            onClick={() =>
                                                store.toggleInactive()
                                            }
                                        />
                                    }
                                    label={`Show inactive (${inactiveCount})`}
                                />
                            )}
                            {store.tab === "flow" && (
                                <Link
                                    to={`/admin/organizations/${id}/kbo-flow-subscriptions`}
                                >
                                    Manage in KBo › Subscriptions
                                </Link>
                            )}
                        </Stack>
                    </Stack>
                    <AcxDataGrid dataGridStore={store.grid} />
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: theme.palette.gray[500],
                            mt: 1,
                        }}
                    >
                        {footnotes[store.tab]}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
});

export default OrganizationUsageValue;
